import classnames from 'classnames';
import React from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import {
  Config,
  GraphApiKeyRoleOption,
  UserRoleOption,
} from 'src/lib/config/config';
import { UserPermission } from 'src/lib/graphqlTypes/types';

const USER_ON_GRAPH_ROLE_DESCRIPTIONS: Record<UserRoleOption, string> = {
  [UserPermission.BILLING_MANAGER]:
    'Can manage org-level configuration and billing. Can remove members but not invite them. No access to graphs.',
  [UserPermission.CONSUMER]:
    'Limited read-only user. Can use the Explorer, Schema Reference, and Changelog, but cannot see any graph metrics.',
  [UserPermission.OBSERVER]:
    'Read-only user. Can view all schemas and metrics on this graph.',
  [UserPermission.DOCUMENTER]:
    'Can view all graph data and edit the README, graph description, and similar non-operational metadata.',
  [UserPermission.CONTRIBUTOR]: `Can view all graph data and push new schema versions to this graph, but cannot manage graph configurations.`,
  [UserPermission.GRAPH_ADMIN]:
    'Can view and manage configuration for this graph, but cannot manage members.',
  [UserPermission.ORG_ADMIN]:
    'Can manage the entire organization, including its members, graphs, and configuration.',
};

const USER_IN_ORG_ROLE_DESCRIPTIONS: Record<UserRoleOption, string> = {
  [UserPermission.BILLING_MANAGER]:
    'Can manage org-level configuration and billing. Can remove members but not invite them. No access to graphs.',
  [UserPermission.CONSUMER]:
    'Limited read-only user. Can use the Explorer, Schema Reference, and Changelog, but cannot see any graph metrics.',
  [UserPermission.OBSERVER]:
    'Read-only user. Can view schemas and metrics on all graphs in the organization.',
  [UserPermission.DOCUMENTER]:
    'Can view all graph data and edit the README, graph description, and similar non-operational metadata.',
  [UserPermission.CONTRIBUTOR]: `Can view all graph data and push new schema versions to graphs in the org, but cannot manage graph configurations.`,
  [UserPermission.GRAPH_ADMIN]:
    'Can view and manage configuration for every graph in the organization, but cannot manage members and billing.',
  [UserPermission.ORG_ADMIN]:
    'Can manage the entire organization, including its members, graphs, and configuration.',
};

const TOKEN_ROLE_DESCRIPTIONS: Record<GraphApiKeyRoleOption, string> = {
  [UserPermission.PERSISTED_QUERY_PUBLISHER]:
    'Limited key that can publish operations to a persisted query list with the CLI or API, but has no read capabilities.',
  [UserPermission.CONSUMER]:
    'Limited read-only key that can only query schemas from your graph (useful in developer tooling).',
  [UserPermission.OBSERVER]:
    'Read-only key to query both schemas and metrics from your graph.',
  [UserPermission.DOCUMENTER]:
    'Use this key to edit the README, graph description, and similar non-operational metadata.',
  [UserPermission.CONTRIBUTOR]: `Use this key to push metrics to your graph and to push schemas to non-protected variants, or if you’re generating a key for a deployed Router or Server.`,
  [UserPermission.GRAPH_ADMIN]:
    'Use this key to push schemas to protected variants or to update configuration with the CLI or API.',
};

/**
 * If showing "user" roles, exclude graph API key options and
 * allow any 'user-on-graph' or 'user-in-org' targets.
 * If showing "graph token" roles, include only non-legacy token roles
 * and only allow "graph" target.
 */
type RoleSelectOptionsProps =
  | {
      target: 'user-on-graph' | 'user-in-org';
      option: UserRoleOption;
      disabled?: boolean;
      selected?: boolean;
    }
  | {
      target: 'graph';
      option: GraphApiKeyRoleOption;
      disabled?: boolean;
      selected?: boolean;
    };

export const RoleSelectOptions = ({
  option,
  target,
  disabled,
  selected,
}: RoleSelectOptionsProps) => {
  return (
    <div
      className="group py-1"
      style={{ whiteSpace: 'normal', overflow: 'auto' }}
    >
      <h3 className="text-base">{Config.roleNameByRoleEnum[option]}</h3>
      <p
        className={classnames(
          !selected &&
            (disabled
              ? 'text-placeholder'
              : 'text-secondary group-hover:text-secondary'),
        )}
      >
        {target === 'graph'
          ? TOKEN_ROLE_DESCRIPTIONS[option]
          : target === 'user-in-org'
            ? USER_IN_ORG_ROLE_DESCRIPTIONS[option]
            : target === 'user-on-graph'
              ? USER_ON_GRAPH_ROLE_DESCRIPTIONS[option]
              : assertUnreachable(target)}
      </p>
    </div>
  );
};
